import { $isElementNode, ElementNode, LexicalNode } from 'lexical';

import { CitationNode } from './CitationNode';
import { formatCitationToken } from './citationTokens';

function $containsCitationNode(node: ElementNode): boolean {
  return node
    .getChildren()
    .some(
      (child) =>
        child instanceof CitationNode ||
        ($isElementNode(child) && $containsCitationNode(child))
    );
}

/**
 * Like Lexical's built-in `getTextContent()`, except CitationNodes (decorator
 * nodes that contribute no text by default) serialize in place as their
 * inline [citations=[...]] token, so the plain text pastes back into a
 * Luminary editor with citation chips restored (see CitationPastePlugin).
 *
 * Subtrees without citations delegate to the native `getTextContent()`, so
 * their output is byte-identical to Lexical's — including elements with
 * custom text serialization such as tables. Only on the path down to a
 * citation does this mirror ElementNode.getTextContent()'s own algorithm:
 * concatenate children, appending a blank line after each non-inline element
 * child except the last.
 */
export function $getTextContentWithCitationTokens(node: LexicalNode): string {
  if (node instanceof CitationNode) {
    return formatCitationToken(node.getParagraphIds());
  }
  if (!$isElementNode(node) || !$containsCitationNode(node)) {
    return node.getTextContent();
  }

  let text = '';
  const children = node.getChildren();
  children.forEach((child, index) => {
    text += $getTextContentWithCitationTokens(child);
    if (
      $isElementNode(child) &&
      !child.isInline() &&
      index !== children.length - 1
    ) {
      text += '\n\n';
    }
  });
  return text;
}
